import { Entity, PaginatedResult, Paging } from './model'
import { throwInvalidParameter } from './errors'
import { trCore } from './coreDicts'
import { DateTimeParseException, LocalTime } from '@js-joda/core'

export type Json = undefined | null | string | boolean | number | Json[] | { [key: string | symbol]: Json }

/**
 * Inplace typesafe creation of object
 *
 * const obj = createObj<User>({login: 'aaa', password: 'bbb'});
 */
export function createObj<T>(obj: T): T {
  return obj
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PartialRecord<K extends keyof any, T> = {
  [P in K]?: T
}

export function getPage<T>(data: T[], paging?: Paging): PaginatedResult<T> {
  const from = paging?.from ?? 0
  const count = Math.min(paging?.count ?? data.length - from, data.length - from)
  return {
    total: data.length,
    from,
    count,
    items: data.slice(from, from + count),
  }
}

export function delay(timeoutInMsec: number): Promise<void> {
  return new Promise<void>(resolve => {
    setTimeout(() => {
      resolve()
    }, timeoutInMsec)
  })
}

export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init?: RequestInit & { timeoutMs?: number },
): Promise<Response> {
  const timeout = init?.timeoutMs ?? 5000
  const controller = new AbortController()
  const id = setTimeout(() => controller.abort(), timeout)
  const response = await fetch(input, {
    ...init,
    signal: controller.signal,
  })
  clearTimeout(id)
  return response
}

/**
 * Replaces string insertions `{[0-9]+}` with the `arg` of same index.
 *
 * @example stringFormat('Hello {0}!', 'John')
 */
export function formatString(str: string, ...args: unknown[]) {
  return str.replace(/{(\d+)}/g, (s, number) => {
    const value =
      args[number] ?? throwInvalidParameter(`<08bd5a9d> Arg with number=${number} not provided for text=${str}`)
    return value.toString()
  })
}

export function runIfDefined<T, R>(
  v: T | undefined | null,
  block: (v: NonNullable<T>) => R | undefined,
): R | undefined {
  if (v !== undefined && v !== null) return block(v)
  return
}

export function map<T, R>(v: T, block: (v: T) => R): R {
  return block(v)
}

export function run<T>(v: T, block: (v: T) => void): T {
  block(v)
  return v
}

export async function runAsync<T>(v: T, block: (v: T) => Promise<void>): Promise<T> {
  await block(v)
  return v
}

interface Property {
  name: string
  descriptor: PropertyDescriptor
}

export function enumObjProps(obj: unknown): Property[] {
  const properties: { [name: string]: PropertyDescriptor } = {}
  let currentObj = obj
  do {
    Object.assign(properties, Object.getOwnPropertyDescriptors(currentObj))
  } while ((currentObj = Object.getPrototypeOf(currentObj)))
  return Object.keys(properties).map(name => <Property>{ name, descriptor: properties[name] })
}

export function getObjectKeys<Key extends string | number | symbol>(obj: Record<Key, unknown>): Key[] {
  return Object.keys(obj) as Key[]
}

/** It can be used with filter and removes undefined from type
 *
 * @example
 * [1, undefined, null].filter(notNull) // returns number[]
 */
export function notNull<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined
}

/**
 * Concats path parts to full path with delimiter '/'
 *
 * @example
 * concatUriPaths('/api/v1/', '/users', id) // => /api/v1/users/123
 */
export function concatUriPaths(...paths: string[]): string {
  switch (paths.length) {
    case 0:
      return ''
    case 1:
      return paths[0]
    case 2:
      return `${paths[0].replace(/\/+$/, '')}/${paths[1].replace(/^\/+/, '')}`
    default:
      return concatUriPaths(...[concatUriPaths(paths[0], paths[1]), ...paths.slice(2, paths.length)])
  }
}

interface Dto {
  id: string
  createdAt: string
  deletedAt?: string
}

export function mapDtoToEntity<E extends Entity, D extends Omit<E, keyof Dto | 'eventId'>>(dto: D & Dto): E {
  return {
    ...dto,
    id: dto.id,
    createdAt: new Date(dto.createdAt),
    deletedAt: runIfDefined(dto.deletedAt, d => new Date(d)),
  } as unknown as E
}

export function parseBool(s?: string): boolean {
  if (!s) return false
  const v = s.toLowerCase()
  if (v === 'true' || v === 'yes' || v === '1' || v === 'on') return true
  if (v === 'false' || v === 'no' || v === '0' || v === 'off') return false
  throwInvalidParameter(
    trCore(d => d.tools.invalidBooleanValueError, s),
    { uid: '<97b3d050>' },
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isPojo(v: any): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && v.constructor === {}.constructor
}

export function lazy<T>(factory: () => T): () => T {
  let v: T | undefined
  return () => {
    if (v !== undefined) return v
    v = factory()
    return v
  }
}

export function lazyAsync<T>(factory: () => Promise<T>): () => Promise<T> {
  let v: Promise<T> | undefined
  return () => {
    if (v !== undefined) return v
    v = factory()
    return v
  }
}

export function normalizeAnswer(answer: string) {
  return answer.replace(/ +/g, ' ').trim().toLowerCase()
}

export function localTimeParse(str: string): LocalTime {
  try {
    return LocalTime.parse(str)
  } catch (e) {
    if (e instanceof DateTimeParseException) {
      throwInvalidParameter(
        trCore(d => d.tools.invalidTimeValueError, str),
        { uid: '<5e8f740e>' },
      )
    }
    throw e
  }
}

export function mapFrom<K, V>(collection: Iterable<V> | IterableIterator<V>, getKey: (v: V) => K): Map<K, V> {
  const result = new Map<K, V>()
  for (const v of collection) {
    result.set(getKey(v), v)
  }
  return result
}

export function getUpdatedString(newVal?: null | string, oldVal?: string): string | undefined {
  if (newVal === null || newVal === '') {
    return undefined
  }

  if (newVal === undefined) {
    return oldVal
  }

  return newVal
}

export function getFirstOrUndefined<T>(values: T[]): T | undefined {
  return values.length > 0 ? values[0] : undefined
}
